import React, { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLiveStreamUrls, getYouTubeVideoId } from "../utils/common";
import VideoPlayer from "../components/video-player";
import { Grid3X3, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import {
    db,
    getLiveStream,
    liveStreamTicketsCollection,
} from "../utils/firebase";
import { EVENT_PATH } from "../App";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { useAuth } from "../contexts/auth-context";

const LiveStream = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [hideAlertMessage, setHideAlertMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);
    const code = searchParams.get("code");

    const [ytVideoIds, setYtVideoIds] = useState([]);
    const [fullscreenIndex, setFullscreenIndex] = useState(null);

    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role?.toUpperCase() === "ADMIN";

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                if (isAdmin) {
                    const eventRef = doc(db, "events", String(id));
                    const snap = await getDoc(eventRef);
                    if (!snap.exists()) {
                        console.error(t("liveStream.errors.documentNotFound"));
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    const urls = getLiveStreamUrls(snap.data()?.liveStreamUrl);
                    if (!urls.length) {
                        console.error(t("liveStream.errors.urlNotFound"));
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    const videoIds = urls
                        .map((url) => getYouTubeVideoId(url))
                        .filter(Boolean);
                    if (!videoIds.length) {
                        console.error(
                            t("liveStream.errors.invalidVideoId"),
                            urls[0]
                        );
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    setYtVideoIds(videoIds);
                    setToken(null);
                } else {
                    const liveStreamTicketResult = await getLiveStream(
                        id,
                        code
                    );

                    if (!liveStreamTicketResult) {
                        console.error(t("liveStream.errors.ticketNotFound"));
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    const token = liveStreamTicketResult.token;
                    const urls =
                        liveStreamTicketResult.liveStreamUrls?.length > 0
                            ? liveStreamTicketResult.liveStreamUrls
                            : getLiveStreamUrls(
                                  liveStreamTicketResult.liveStreamUrl
                              );

                    if (!urls.length) {
                        console.error(t("liveStream.errors.urlNotFound"));
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    const videoIds = urls
                        .map((url) => getYouTubeVideoId(url))
                        .filter(Boolean);
                    if (!videoIds.length) {
                        console.error(
                            t("liveStream.errors.invalidVideoId"),
                            urls[0]
                        );
                        navigate(`${EVENT_PATH.replace(":id", id)}`);
                        return;
                    }

                    setYtVideoIds(videoIds);
                    sessionStorage.setItem("token", token);
                    setToken(token);
                }
            } catch (error) {
                console.error(t("liveStream.errors.fetchError"), error);
                setError(error.message || t("liveStream.errors.loadFailed"));
            } finally {
                setIsLoading(false);
            }
        })();
    }, [id, code, navigate, t, isAdmin]);

    useEffect(() => {
        let unsubscribe;

        if (!isAdmin && token && code && id) {
            let isFirstMismatch = true;
            try {
                // Ensure parameters are strings
                const eventId = String(id);
                const ticketCode = String(code);
                const docId = `${eventId}_${ticketCode}`;

                const docRef = doc(db, "live-stream-tickets", docId);

                unsubscribe = onSnapshot(
                    docRef,
                    (docSnapshot) => {
                        try {
                            if (!docSnapshot.exists()) {
                                console.error(
                                    t("liveStream.errors.documentNotFound")
                                );
                                navigate(`/event/${eventId}`);
                                return;
                            }

                            const liveStreamTicketData = docSnapshot.data();

                            if (!liveStreamTicketData) {
                                console.error(t("liveStream.errors.dataNull"));
                                navigate(`/event/${eventId}`);
                                return;
                            }

                            const serverToken = liveStreamTicketData.token;
                            if (!serverToken || serverToken !== token) {
                                if (isFirstMismatch) {
                                    isFirstMismatch = false;
                                } else {
                                    console.error(
                                        t("liveStream.errors.tokenMismatch")
                                    );
                                    navigate(`/event/${eventId}`);
                                    return;
                                }
                            }
                        } catch (error) {
                            console.error(
                                t("liveStream.errors.snapshotError"),
                                error
                            );
                            navigate(`/event/${eventId}`);
                        }
                    },
                    (error) => {
                        console.error(t("liveStream.errors.fetchError"), error);
                        navigate(`/event/${eventId}`);
                    }
                );
            } catch (error) {
                console.error(t("liveStream.errors.useEffectError"), error);
            }
        }
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [token, code, id, navigate, t, isAdmin]);

    // Check if code is present (after all hooks are called)
    if (!code && !isAdmin) {
        return (
            <div className="page-size flex-center p-4">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                    <div className="text-error text-6xl">🔒</div>
                    <h2 className="text-xl font-bold">
                        {t("liveStream.ui.accessDenied")}
                    </h2>
                    <p className="text-gray-600">
                        {t("liveStream.ui.noTicketCode")}
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            navigate(`${EVENT_PATH.replace(":id", id)}`)
                        }
                    >
                        {t("liveStream.ui.backToEvent")}
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="page-size flex-center p-4">
                <div className="flex flex-col items-center gap-4">
                    <div className="loading loading-bars loading-lg"></div>
                    <p className="text-lg font-semibold">
                        {t("liveStream.ui.loading")}
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-size flex-center p-4">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                    <div className="text-error text-6xl">⚠️</div>
                    <h2 className="text-xl font-bold">
                        {t("liveStream.ui.error")}
                    </h2>
                    <p className="text-gray-600">{error}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            navigate(`${EVENT_PATH.replace(":id", id)}`)
                        }
                    >
                        {t("liveStream.ui.backToEvent")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-size flex-center p-4">
            <div className="flex flex-col gap-2 w-full max-w-[1000px]">
                {!hideAlertMessage && (
                    <div className="flex flex-col gap-2 border border-error rounded-md p-2 text-error my-4">
                        <div className="flex flex-row justify-between items-start">
                            <p className="inline-flex items-center gap-2 py-1">
                                <TriangleAlert className="min-w-6" />
                                <span className="align-middle">
                                    {t("liveStream.warning.title")}
                                </span>
                            </p>
                            <button
                                className="btn btn-ghost btn-square btn-sm"
                                onClick={() => setHideAlertMessage(true)}
                            >
                                <X />
                            </button>
                        </div>
                        <p>{t("liveStream.warning.message")}</p>
                    </div>
                )}
                <p className="font-bold text-xl">{t("liveStream.ui.title")}</p>

                {ytVideoIds.length > 0 ? (
                    fullscreenIndex !== null ? (
                        <div className="w-full flex flex-col gap-2">
                            <div className="w-full border aspect-video">
                                <VideoPlayer
                                    videoId={ytVideoIds[fullscreenIndex]}
                                    className="relative w-full h-full"
                                    showController={false}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline btn-sm w-fit"
                                onClick={() => setFullscreenIndex(null)}
                            >
                                <Grid3X3 size={18} className="mr-1" />
                                Back to grid
                            </button>
                        </div>
                    ) : ytVideoIds.length === 1 ? (
                        <div className="w-full border">
                            <VideoPlayer
                                videoId={ytVideoIds[0]}
                                className="relative w-full h-full"
                                showController={false}
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                            {ytVideoIds.map((videoId, i) => (
                                <div
                                    key={i}
                                    className="w-full border aspect-video flex flex-col gap-1"
                                >
                                    <VideoPlayer
                                        videoId={videoId}
                                        className="relative w-full h-full"
                                        showController={false}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-xs w-fit"
                                        onClick={() => setFullscreenIndex(i)}
                                    >
                                        Fullscreen
                                    </button>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="w-full border p-8 text-center">
                        <p className="text-error">
                            {t("liveStream.ui.invalidStream")}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveStream;
